/**
 * The Reach's station services as tabs of M2's station screen (M3 plan R22f, §7 item 3). The career in the Reach
 * docks through M2 (F is M2's dock request and auto-dock; the Reach's hail is off, `ReachCoreOptions.hail`), so M2's
 * station screen hosts M3's `StationServicePanel`s through the same registry M3's hail panel used:
 *
 * - The docked M2 station is found among the current system's sites: the home cluster's stations keep M2's ids (the
 *   site's type, R22a), every other site's M2 id is its slug (`m2StationId`).
 * - Its tabs are the registry's panels for that site in panel order (Charts at a `chart-buyer`, Parts at a
 *   `parts-vendor`, Home everywhere), minus the panels M2's own tabs replace: outfitting sells and fits scanners, so
 *   M3's Scanner tab is left out. Parts are not wares (D-TBD 40); while the goods market runs (CL-5, core-loop plan
 *   §4: the strategic market and `career.json goods.tradeEnabled`), the Market's Goods rows trade the same
 *   `cargo.parts` block, so Parts folds into the Market (`GOODS_MARKET_PANELS`). Without it the Parts tab stays and
 *   buys into the career's parts block through the `CareerPort` (the `buyParts` command).
 * - A mounted panel gets the Reach core and the site; its `refresh` re-reads its buttons and `close` goes back to
 *   M2's own tab.
 *
 * - CL-6: `signals` reads the tab promotion rules' Reach state, read-only: chart data this station buys (unsold charts
 *   at a `chart-buyer`, not counting the home system's: a new career charts it on entry, so it would promote Charts on
 *   minute one), a bounty on the pilot (the strategic relations mirror; the Registry pays them as fines), a
 *   founded corporation (the player slot in the relation pairs), and the Wire's unread count.
 *
 * UI events only (list and signals run on a station screen render).
 */
import type { StationSite, SystemLayout } from '../../galaxy/layout/types'
import type { StationServicePanel, StationServiceRegistry } from '../../reach/ports'
import type { ReachCore } from '../../reach/runtime/types'
import { unsoldCharts } from '../../reach/state/charts'
import type { RelationsView } from '../../strategic/types'
import type { ExternalTab, ExternalTabSource } from '../../ui/external'
import type { TabReachSignals } from '../../ui/models/stationTabs'
import { m2StationId } from './stations'

/** M3 panels an M2 tab replaces (M2's outfitting sells the scanner Mk). */
export const REPLACED_PANELS: readonly string[] = Object.freeze(['scanner'])

/** M3 panels the Market's Goods rows replace while the goods market runs (CL-5: Parts folds into the Market). */
export const GOODS_MARKET_PANELS: readonly string[] = Object.freeze(['parts'])

/** The panels M2's tabs replace: `REPLACED_PANELS`, plus `GOODS_MARKET_PANELS` while the goods market runs. */
export function replacedPanels(goodsMarketRuns: boolean): readonly string[] {
  return goodsMarketRuns ? [...REPLACED_PANELS, ...GOODS_MARKET_PANELS] : REPLACED_PANELS
}

/** The site of the docked M2 station `stationId` in `layout`, or null when it is not one of this system's. */
export function siteOfStation(layout: Pick<SystemLayout, 'stations' | 'home'> | null, stationId: string): StationSite | null {
  if (layout === null) return null
  for (const site of layout.stations) {
    if (m2StationId(site.id) === stationId) return site
  }
  if (layout.home === null) return null
  for (const site of layout.stations) {
    if (site.type === stationId) return site
  }
  return null
}

/** What the tabs read from the Reach runtime. */
export interface StationTabsReach {
  readonly core: ReachCore
  readonly services: StationServiceRegistry
  /** CL-5: whether the Market's goods rows run (strategic market and `goods.tradeEnabled`); absent = they don't. */
  readonly goodsMarketRuns?: () => boolean
  /** CL-6: the strategic relations mirror (bounties, the player corporation's pairs); absent or null = none. */
  readonly relations?: () => RelationsView | null
  /** CL-6: notable Wire items not yet read; absent = 0. */
  readonly wireUnread?: () => number
}

/** A bounty stands on the pilot with any faction (the Registry's fines). */
export function hasBounty(view: RelationsView | null): boolean {
  if (view === null) return false
  return Object.values(view.player.bounties).some((b) => b !== undefined && b > 0)
}

/** The player corporation is founded: it appears as `'player'` in the relation pairs (M6). */
export function hasCorporation(view: RelationsView | null): boolean {
  if (view === null) return false
  return view.pairs.some((p) => p.a === 'player' || p.b === 'player')
}

/** Unsold chart value from a system other than the home system (the career's starting charts don't count). */
export function hasNewCharts(core: Pick<ReachCore, 'state' | 'galaxy'>): boolean {
  return unsoldCharts(core.state).some(([systemId]) => systemId !== core.galaxy.homeSystemId)
}

/** CL-6: the tab promotion signals at the docked M2 station `stationId` (null when it is not one of this system's). */
export function tabSignals(reach: StationTabsReach, stationId: string): TabReachSignals | null {
  const site = siteOfStation(reach.core.session.layout, stationId)
  if (site === null) return null
  const relations = reach.relations?.() ?? null
  return {
    chartData: site.services.includes('chart-buyer') && hasNewCharts(reach.core),
    finesOrBounties: hasBounty(relations),
    corporation: hasCorporation(relations),
    wireUnread: reach.wireUnread?.() ?? 0,
  }
}

function tabOf(panel: StationServicePanel, core: ReachCore, station: StationSite): ExternalTab {
  return {
    id: panel.id,
    title: panel.title,
    mount: (el, host) => panel.mount(el, { core, station, close: () => host.close(), refresh: () => host.refresh() }),
  }
}

/** The external tab source M2's station screen takes in the Reach. */
export function createReachStationTabs(reach: StationTabsReach): ExternalTabSource {
  return {
    list(stationId: string): readonly ExternalTab[] {
      const core = reach.core
      const site = siteOfStation(core.session.layout, stationId)
      if (site === null) return []
      const out: ExternalTab[] = []
      const replaced = replacedPanels(reach.goodsMarketRuns?.() ?? false)
      for (const panel of reach.services.list(site)) {
        if (!replaced.includes(panel.id)) out.push(tabOf(panel, core, site))
      }
      return out
    },
    signals: (stationId: string): TabReachSignals | null => tabSignals(reach, stationId),
  }
}
